import { LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import React from "react";
import { Page } from "../../activities/AppPage";
import { PatientId, PatientTaskBroadcastDetails } from "../../messages";
import { DaySlot } from "../../SelectTimeSlot";
import { AgencyMember } from "../../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { scrollToElement } from "../../utils/scrollToElement";
import { shakeElement } from "../../utils/shakeElement";
import { AgencyDetailsCard } from "../AgencyDetailsCard";
import FinishOnboardingBeforeStaffingModal from "../FinishOnboardingBeforeStaffingModal";
import PatientAvatar from "../PatientAvatar/PatientAvatar";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import AvailableTaskDetails from "./components/AvailableTaskDetails";
import AvailableTaskFooter from "./components/AvailableTaskFooter";
import AvailableTaskRequiredDocuments from "./components/AvailableTaskRequiredDocuments";
import OnboardingTaskDetails from "./components/OnboardingTaskDetails";
import SelectTaskTimeSlotCard from "./components/SelectTaskTimeSlotCard";
import { OutsourcingAgencyBanner } from "../OutsourcingAgencyBanner/OutsourcingAgencyBanner";
import { API } from "../../API";
import { useOutsourcingAgencyQuery } from "../OutsourcingAgencyBanner/useOutsourcingAgencyQuery";

type BaseProps = {
  caregiver: AppCaregiver;
  agencyMember: AgencyMember;
  taskBroadcastDetails: PatientTaskBroadcastDetails;
  selectedDate: LocalDate | null;
  selectedTime: LocalTime | null;
  selectedDateTime: LocalDateTime | null;
  shouldShowError: boolean;
  shouldShowTimeSlotError: boolean;
  timeSlotAppCardRef: React.RefObject<HTMLDivElement> | null;
  selectDateLabelRef: React.RefObject<HTMLDivElement> | null;
  selectTimeLabelRef: React.RefObject<HTMLDivElement> | null;
  onSelectDate: (date: LocalDate) => void;
  onSelectTime: (time: LocalTime) => void;
  onShowError: () => void;
  onClickLocation: () => void;
  onClickGoBack: () => void;
  onClickChat: (patientId: PatientId) => void;
  onClickCall: (phonenumber: string | null) => void;
  onSchedule: (datetime: LocalDateTime) => void;
  onRequestNavigate: (page: Page) => void;
  onRequestOutsourcingAgencyData: API["getOutsourcingAgencyData"];
};

type Props = BaseProps &
  (
    | {
        slotsStatus: "loading";
      }
    | {
        slotsStatus: "loaded";
        slots: DaySlot[];
        message: string | null;
      }
    | {
        slotsStatus: "error";
      }
  );

export const AvailableTaskV2Screen = (props: Props) => {
  const [onboardingModalOpen, setOnboardingModalOpen] = React.useState(false);

  const outsourcingAgencyQuery = useOutsourcingAgencyQuery({
    onRequestOutsourcingAgencyData: props.onRequestOutsourcingAgencyData,
    patientId: props.taskBroadcastDetails.patientId,
  });

  const handleClickChat = () => {
    props.onClickChat(props.taskBroadcastDetails.patientId);
  };

  const handleClickCall = () => {
    props.onClickCall(props.taskBroadcastDetails.agency.officePhone);
  };

  const handleClickLinkedPatient = () => {
    if (props.taskBroadcastDetails.linkedPatientTaskInstanceId !== null) {
      props.onRequestNavigate({
        type: "AvailableTaskScreen",
        taskBroadcastId: props.taskBroadcastDetails.linkedPatientTaskInstanceId,
      });
    }
  };

  const handleClickTakeThisTask = () => {
    if (props.taskBroadcastDetails.type === "Onboarding") {
      setOnboardingModalOpen(true);
      return;
    }

    if (props.selectedDate === null) {
      props.onShowError();
      if (props.selectDateLabelRef !== null && props.timeSlotAppCardRef !== null) {
        shakeElement(props.selectDateLabelRef);
        scrollToElement(props.timeSlotAppCardRef);
      }
      return;
    }

    if (props.selectedTime === null) {
      props.onShowError();
      if (props.selectTimeLabelRef !== null && props.timeSlotAppCardRef !== null) {
        shakeElement(props.selectTimeLabelRef);
        scrollToElement(props.timeSlotAppCardRef);
      }
      return;
    }

    props.onSchedule(props.selectedDate.atTime(props.selectedTime));
  };

  return (
    <Scaffold
      title={<ScaffoldTitle label={props.taskBroadcastDetails.title} />}
      endActions={
        <PatientAvatar
          key={props.taskBroadcastDetails.patientInitials}
          initials={props.taskBroadcastDetails.patientInitials}
        />
      }
      footer={
        <AvailableTaskFooter
          selectedDateTime={props.selectedDateTime}
          onClickApprove={handleClickTakeThisTask}
        />
      }
      onClickGoBack={props.onClickGoBack}
      className="AvailableTaskScreen"
    >
      <OutsourcingAgencyBanner
        context="Visit"
        patientId={props.taskBroadcastDetails.patientId}
        onRequestOutsourcingAgencyData={props.onRequestOutsourcingAgencyData}
      />
      {(() => {
        switch (props.taskBroadcastDetails.type) {
          case "Available":
            return (
              <AvailableTaskDetails
                {...props.taskBroadcastDetails}
                onClickLocation={props.onClickLocation}
                onClickLinkedPatient={handleClickLinkedPatient}
              />
            );

          case "Onboarding":
            return (
              <OnboardingTaskDetails
                {...props.taskBroadcastDetails}
                onClickLocation={props.onClickLocation}
                onClickPatientSchedule={() =>
                  props.onRequestNavigate({
                    type: "PatientSchedule",
                    patientTaskInstanceId: props.taskBroadcastDetails.taskInstanceId,
                  })
                }
              />
            );
        }
      })()}

      {props.taskBroadcastDetails.type === "Available" &&
        (() => {
          switch (props.slotsStatus) {
            case "error":
              return <SelectTaskTimeSlotCard status="error" />;

            case "loading":
              return <SelectTaskTimeSlotCard status="loading" />;

            case "loaded":
              return (
                <SelectTaskTimeSlotCard
                  status="loaded"
                  slots={props.slots}
                  message={props.message}
                  selectedDate={props.selectedDate}
                  selectedTime={props.selectedTime}
                  shouldShowError={props.shouldShowError}
                  timeSlotAppCardRef={props.timeSlotAppCardRef ?? undefined}
                  selectDateLabelRef={props.selectDateLabelRef ?? undefined}
                  selectTimeLabelRef={props.selectTimeLabelRef ?? undefined}
                  onSelectDate={props.onSelectDate}
                  onSelectTime={props.onSelectTime}
                />
              );
          }
        })()}

      <AvailableTaskRequiredDocuments documents={props.taskBroadcastDetails.documents} />

      {outsourcingAgencyQuery.isSuccess && (
        <AgencyDetailsCard
          agency={outsourcingAgencyQuery.data ?? props.taskBroadcastDetails.agency}
          coordinatorInCharge={props.agencyMember}
          onClickChat={handleClickChat}
          onClickCall={handleClickCall}
          showCallButton={outsourcingAgencyQuery.data === null}
        />
      )}

      <FinishOnboardingBeforeStaffingModal
        isOpen={onboardingModalOpen}
        onRequestClose={() => setOnboardingModalOpen(false)}
        onClickFinishOnboarding={() => {
          setOnboardingModalOpen(false);
          props.onRequestNavigate({ type: "OnboardingActivity" });
        }}
      />
    </Scaffold>
  );
};
